import { useEffect, useState } from "react"

import { App, Flex, Form, message, Typography, Upload } from "antd"
import dayjs from "dayjs"

import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import ButtonComponent from "../Button"
import TextAreaComponent from "../Input/TextArea"
import SelectComponent from "../Select"
import { AttachmentsList } from "./CommentFormAttachments"

const { Text, Link } = Typography

const MAX_TOTAL_SIZE_MB = 10

function CommentForm({
  onClose,
  onSubmit,
  isDisabledSubmitBtn,
  isSubmitting = false,
  onCommentDelete,
  onDeleteAttachment,
  formRef,
  tabKeyType,
  isBaCommetsModal,
  userInfoComment,
  userCommentData,
  userBusinessAccountsList, //only for user table modal
  setCurrentBA //only for user table modal
}) {
  const [form] = Form.useForm()
  const { modal } = App.useApp()

  const [uploadedFiles, setUploadedFiles] = useState([])
  const [addComment, setAddComment] = useState(userCommentData?.comment_text)
  const [editComment, setEditComment] = useState(false)

  const isBusinessAccountForm = ["user_business_accounts", "business_accounts_owner"].includes(
    tabKeyType
  ) //right tab form
  const formTypeData = { isBusinessAccountForm, tabKeyType }

  const initialValues = {
    comment: userCommentData?.comment_text,
    attachments: userCommentData?.attachments,
    business_account: userBusinessAccountsList?.[0]?.id
  }

  // userCommentData loads asynchronously after the form has already mounted (the modal opens
  // before the comment fetch resolves), so AntD's one-time `initialValues` snapshot misses it -
  // sync the form and the "has a comment" state explicitly once the data arrives.
  useEffect(() => {
    if (!userCommentData) return
    form.setFieldsValue({
      comment: userCommentData?.comment_text,
      attachments: userCommentData?.attachments
    })
    setAddComment(!!userCommentData?.comment_text)
  }, [userCommentData])
  // attachments that are already saved on server
  // const [existingAttachments, setExistingAttachments] = useState(initialValues?.attachments || []);

  // useEffect(() => {
  //   setExistingAttachments(userData?.file_list || []);
  // }, [userData]);

  const availableCountFiles = 20

  // Add a flag to track the display of messages
  let hasShownSizeError = false
  let hasShownFormatError = false

  const beforeUpload = (file, fileList) => {
    const allowedExtensions = [
      "png",
      "jpeg",
      "jpg",
      "pdf",
      "txt",
      "doc",
      "docx",
      "odt",
      "rtf",
      "xls",
      "xlsx"
    ]
    const fileExtension = file.name.split(".").pop().toLowerCase()

    // Check the format for each file
    if (!allowedExtensions.includes(fileExtension)) {
      // Show the format error only once per pack
      if (!hasShownFormatError) {
        message.error(`${file.name} has an unsupported format.`)
        hasShownFormatError = true
      }
      return Upload.LIST_IGNORE
    }

    // Reset flags for new size check
    if (fileList.length > 1 && fileList.indexOf(file) === 0) {
      hasShownSizeError = false
      hasShownFormatError = false
    }

    // Calculate the total size
    const currentBatchSize = fileList.reduce((acc, currFile) => acc + (currFile.size || 0), 0)
    const totalSize =
      uploadedFiles.reduce((acc, currFile) => acc + (currFile.size || 0), 0) + currentBatchSize

    if (totalSize / 1024 / 1024 > MAX_TOTAL_SIZE_MB) {
      // Show the size error only once per pack
      if (!hasShownSizeError) {
        message.error(`Total file size should not exceed ${MAX_TOTAL_SIZE_MB} MB.`)
        hasShownSizeError = true
      }
      return Upload.LIST_IGNORE
    }

    return false
  }

  const handleUploadChange = (info) => {
    setUploadedFiles(info.fileList)
  }

  const onFinish = (values) => {
    onSubmit(values, formTypeData)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const validateMessages = {
    required: addErrorIcon("Required field was not filled.")
  }

  const normFile = (e) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  const openModalConfirm = () => {
    modal.confirm({
      ...modalConfig("exclamation", 442),
      // centered: !isMobile,
      centered: true,

      title: "Confirm deleting",
      content: (
        <Text type="secondary">
          Are you sure you want to delete this comment? This action cannot be undone. However, you
          can add another comment again later if needed.
        </Text>
      ),
      cancelText: "Cancel",
      okText: "Delete",
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        const data = userCommentData
        onCommentDelete(data, formTypeData)
      }
    })
  }

  const handleDeleteAttachment = (file) => {
    modal.confirm({
      ...modalConfig("exclamation", 442),
      centered: true,
      title: "Delete attachment",
      content: <Text type="secondary">Are you sure you want to delete this saved attachment?</Text>,
      cancelText: "Cancel",
      okText: "Delete",
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        onDeleteAttachment(file, formTypeData)
      }
    })
  }

  function openFileInNewTab(file) {
    const fileUrl = URL.createObjectURL(file)
    window.open(fileUrl, "_blank")
    setTimeout(() => {
      URL.revokeObjectURL(fileUrl)
    }, 1000)
  }

  const handlePreview = (file) => {
    openFileInNewTab(file?.originFileObj)
  }

  return (
    <Form
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      form={form}
      initialValues={initialValues}
      ref={formRef}
      onValuesChange={(changedValues, allValues) => {
        if ("business_account" in changedValues) {
          setCurrentBA(changedValues.business_account)
        }
      }}
    >
      <div style={{ marginTop: 16 }}>
        {isBusinessAccountForm ? ( //right tab
          isBaCommetsModal ? (
            //right tab modal opened from ba page (ba owner)
            <Flex vertical gap={4}>
              <Text type={"secondary"}>User name and email</Text>
              <Text>
                {/* {`${userCommentData?.full_name} ${userCommentData?.email}`} */}
                {`${userInfoComment?.full_name} ${userInfoComment?.email}`}
              </Text>
            </Flex>
          ) : (
            //right tab modal opened from users page
            <Form.Item name="business_account" label="Business account name">
              <SelectComponent
                options={userBusinessAccountsList?.map((el) => ({
                  value: el?.id,
                  label: el?.name
                }))}
                defaultValue={userBusinessAccountsList?.[0]?.id}
              />
            </Form.Item>
          )
        ) : (
          //left tab
          <Flex vertical gap={4}>
            <Text type={"secondary"}>
              {isBaCommetsModal ? "Business account name" : "User name and email"}
            </Text>
            <Text style={{ fontWeight: 500 }}>
              {
                isBaCommetsModal
                  ? `${userInfoComment?.data?.business_account?.name}` //left tab modal opened from ba page
                  : `${userInfoComment?.full_name} ${userInfoComment?.email}` //left tab modal opened from users page
              }
            </Text>
          </Flex>
        )}

        <Form.Item
          name="comment"
          label="Comment"
          style={{
            height: addComment || initialValues?.comment ? "auto" : 20,
            overflow: "hidden",
            transition: "height 0.3s ease",
            marginTop: 16,
            marginBottom: 8,
            position: "relative"
          }}
          labelCol={{
            style: { display: "flex", justifyContent: "space-between", alignItems: "center" }
          }}
          extra={
            <>
              {userCommentData?.comment_text ? (
                <Flex style={{ position: "absolute", right: 0, top: -35 }}>
                  <ButtonComponent
                    type="link"
                    text={editComment ? "Cancel" : "Edit"}
                    onClick={() => {
                      setEditComment(!editComment)
                      form.setFieldsValue({ comment: initialValues?.comment })
                    }}
                  />
                  <ButtonComponent
                    type="link"
                    text="Delete"
                    onClick={() => openModalConfirm()}
                    disabled={!userCommentData?.comment_text}
                  />
                </Flex>
              ) : null}
            </>
          }
        >
          {(addComment || initialValues?.comment) && (
            <TextAreaComponent
              rows={5}
              maxLength={500}
              placeholder={"Write any related information you want..."}
              disabled={editComment || !userCommentData?.comment_text ? false : true}
            />
          )}
        </Form.Item>

        {userCommentData?.last_updated ? (
          <Text type="secondary" style={{ display: "block", fontWeight: 500, marginBottom: 24 }}>
            {`Last updated: ${dayjs(userCommentData?.last_updated).format("DD MMM YYYY h:mm A [(UTC)]")}`}
          </Text>
        ) : null}

        {(addComment || initialValues?.file_list?.length) && (
          <>
            <Flex vertical gap={4} style={{ marginTop: 16, marginBottom: 8 }}>
              <Text>Attach files</Text>
              <Text type={"secondary"} style={{ fontWeight: 300 }}>
                Possible applicable file formats: .png, .jpeg, .jpg,
              </Text>
            </Flex>

            <Form.Item
              label={null}
              name="file_list"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              dependencies={["comment"]}
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value?.length > availableCountFiles) {
                      return Promise.reject(new Error("You can upload a maximum of 20 files."))
                    }
                    return Promise.resolve()
                  }
                },
                // Require EITHER a comment OR at least one file (an OR, not an AND): valid when there
                // is text, a newly attached file, or an already-attached file on an existing comment.
                ({ getFieldValue }) => ({
                  validator() {
                    const hasText = !!String(getFieldValue("comment") ?? "").trim()
                    const hasNewFiles = (getFieldValue("file_list")?.length ?? 0) > 0
                    const hasExistingFiles = (initialValues?.["attachments"]?.length ?? 0) > 0
                    if (hasText || hasNewFiles || hasExistingFiles) return Promise.resolve()
                    return Promise.reject(new Error("Add a comment or attach at least one file."))
                  }
                })
              ]}
            >
              <Upload
                multiple
                maxCount={availableCountFiles}
                accept={".png,.jpeg,.jpg,.pdf,.txt,.doc,.docx,.odt,.rtf,.xls,.xlsx"}
                beforeUpload={beforeUpload}
                onChange={handleUploadChange}
                listType="text"
                // disabled={isAttachDisabled}
                style={{ width: "100%" }}
                iconRender={(file) => (
                  <Icon
                    type={file?.type?.startsWith("image/") ? "pictures" : "document"}
                    style={{ fontSize: 20, lineHeight: 1 }}
                  />
                )}
                showUploadList={{
                  extra: (file) => (
                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                      ({(file?.size / 1000).toFixed(0)}KB)
                      <ButtonComponent
                        type="link"
                        shape="circle"
                        icon={<Icon type="eye" />}
                        onClick={() => handlePreview(file)}
                        className="flex center table-action-btn"
                        style={{ position: "absolute", right: 30, top: 4 }}
                      />
                    </Text>
                  ),
                  showRemoveIcon: true,
                  removeIcon: (
                    <Icon type={"delete"} style={{ fontSize: 20, lineHeight: 1, marginRight: 4 }} />
                  )
                }}
              >
                <ButtonComponent
                  size="large"
                  type="dashed"
                  icon={<Icon type="arrow_out_of_box" />}
                  disabled={isDisabledSubmitBtn}
                  className={"dashed-btn table-action-btn flex center"}
                  block
                >
                  <Text type="secondary">Click to upload files</Text>
                </ButtonComponent>
              </Upload>
            </Form.Item>

            <AttachmentsList
              attachments={initialValues?.["attachments"]}
              onDelete={handleDeleteAttachment}
              preview
            />
          </>
        )}

        {!addComment ? (
          <ButtonComponent
            size="large"
            htmlType="button"
            onClick={() => setAddComment(true)}
            style={{ width: "100%", marginTop: 15 }}
          >
            Add comment
          </ButtonComponent>
        ) : null}
      </div>
      <Flex gap={16} justify="end" style={{ padding: "32px 3px 3px 3px" }}>
        <ButtonComponent size="large" htmlType="button" onClick={onClose}>
          Cancel
        </ButtonComponent>

        <ButtonComponent
          size="large"
          type="primary"
          htmlType="submit"
          loading={isSubmitting}
          disabled={isDisabledSubmitBtn}
        >
          Save
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default CommentForm
